import { LANDING_PATH, navigate } from '../route.ts';
import { ThemeToggle } from '../ThemeToggle.tsx';
import { DRAFT_NOTICE, formatUpdated, type LegalDocument } from './documents.ts';
import { LegalLinks } from './LegalLinks.tsx';
// The public chrome — the top bar, the footer, the brand — belongs to the
// landing page's stylesheet, and these documents are the same kind of page
// addressed to the same kind of visitor. Importing it rather than restating
// `.lp-top` here keeps one definition of what a public page looks like.
import '../landing.css';
import './legal.css';

/**
 * One component renders all three legal documents.
 *
 * `documents.ts` argues why they are data. This is the other half of that
 * argument: the table of contents, the anchors and the section numbering are
 * *derived* from the array rather than maintained alongside it, so a section
 * that exists is a section that can be linked to, and there is no state in
 * which the contents list and the body disagree about what the document
 * contains.
 *
 * **Nothing here calls the API, and that is a requirement rather than an
 * accident.** These pages are read by people with no account: a prospect's
 * procurement team, a data protection officer, somebody who has been asked to
 * accept terms and would like to read them first. A legal document that needs a
 * session, or that renders empty when a server is down, has not been published.
 *
 * The numbering is positional and deliberately not stored in the data. A clause
 * is cited by anchor, which survives a section being inserted above it; the
 * numeral is a reading aid, and one written into the data would be a second
 * ordering free to disagree with the first.
 */

interface LegalPageProps {
  /**
   * Named `doc` rather than `document`, which would shadow the global of that
   * name inside every closure in this file — a rename away from a line that
   * reads correctly and does something else entirely.
   */
  readonly doc: LegalDocument;
}

export const LegalPage = ({ doc }: LegalPageProps) => (
  <div className="landing legal">
    <header className="lp-top">
      <div className="lp-top-inner">
        <a
          className="lp-brand"
          href={LANDING_PATH}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            navigate(LANDING_PATH);
          }}
        >
          DriftWatch
        </a>
        <ThemeToggle />
      </div>
    </header>

    <main className="lp-main legal-main">
      <header className="legal-head">
        <h1 className="legal-title">{doc.title}</h1>
        <p className="legal-summary">{doc.summary}</p>

        {/*
          Version and date together, in the order a questionnaire asks for them,
          and marked up as a definition list because that is what they are: two
          named facts about the document rather than a sentence about it. `time`
          carries the machine-readable date so the page states one date twice in
          one place rather than in two.
        */}
        <dl className="legal-meta">
          <div className="legal-meta-pair">
            <dt>Version</dt>
            <dd className="num">{doc.version}</dd>
          </div>
          <div className="legal-meta-pair">
            <dt>Last updated</dt>
            <dd>
              <time dateTime={doc.updated}>{formatUpdated(doc.updated)}</time>
            </dd>
          </div>
        </dl>

        {doc.draft && (
          <div className="callout callout-caution legal-draft" role="note">
            <span className="callout-title">Draft — not yet in force</span>
            {DRAFT_NOTICE}
          </div>
        )}
      </header>

      <nav className="legal-contents" aria-labelledby="legal-contents-title">
        <h2 className="label" id="legal-contents-title">
          Contents
        </h2>
        <ol className="legal-contents-list">
          {doc.sections.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`}>{section.heading}</a>
            </li>
          ))}
        </ol>
      </nav>

      {doc.sections.map((section, index) => (
        <section key={section.id} id={section.id} className="legal-section">
          <h2 className="legal-heading">
            <span className="legal-number" aria-hidden="true">
              {index + 1}.
            </span>
            {section.heading}
          </h2>
          {section.paragraphs.map((paragraph) => (
            <p key={paragraph} className="legal-body">
              {paragraph}
            </p>
          ))}
          {section.bullets !== undefined && (
            <ul className="legal-bullets">
              {section.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="legal-alongside" aria-labelledby="alongside-title">
        <h2 className="legal-heading" id="alongside-title">
          Read alongside
        </h2>
        <p className="legal-body">
          These three documents are one agreement between us, split by subject rather than written
          to stand alone. The data processing agreement in particular is answered by the privacy
          policy and answers nothing on its own.
        </p>
        <LegalLinks omit={doc.id} label="The other legal documents" />
      </section>
    </main>

    <footer className="lp-foot">
      <div className="lp-foot-inner">
        <span className="lp-brand lp-brand-foot">DriftWatch</span>
        <LegalLinks label="Legal documents" className="legal-links legal-links-foot" />
      </div>
    </footer>
  </div>
);
